/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useRef, useContext } from "react";
import { compose } from "redux";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import ReactJson from "react-json-view";
import classnames from "classnames";
import moment from "moment";
import axios from "axios";
import { Link } from "react-router-dom";

import {
  UPDATE_USER_APPLICATION,
  RESET_USER_APPLICATION,
  REJECT_USER_APPLICATION,
  TEST_UNDERWRITE,
  SET_USER_TAGS,
  RESOLVE_FRAUD_TAG,
  FETCH_BALANCES,
  LOST_CARD_RESEND,
  REISSUE_CARD,
} from "utils/mutations";
import { Container, ModalContext, withModal } from "components/common";
import CreatableSelect from "react-select/creatable";
import ApproveUserModal from "components/Popups/Modals/SupportAutomationModals/ApproveUserModal";
import { COMPLETION_STATUS, DAYS_OF_WEEK } from "utils/constants";
import QUERY from "./query";

import Notes from "../Admin/User/Notes";
import AccountsList from "../Admin/User/AccountsList";
import TWNTable from "../Admin/User/TWNTable";
import FraudDataBox from "../Admin/User/FraudDataBox";
import AssetReportTable from "../Admin/User/AssetReportTable";
import UnderwritingDataBox from "../Admin/User/UnderwritingDataBox";
import UnderwritingDecisionDataBox from "../Admin/User/UnderwritingDecisionDataBox";
import BankIdentityDataBox from "../Admin/User/BankIdentityDataBox";
import IpAddressTable from "../Admin/User/IpAddressTable";
import SettingsButton from "../Settings/common/SettingsButton";
import Address from "../Settings/common/Sections/NewAddress";
import SettingsInputContainer from "../Settings/common/SettingsInputContainer";
import UserRewardPoints from "./UserRewardPoints";
import UpdateUserNames from "./UpdateUserNames";
import { track } from "../../../utils/analytics";
import ApplicationSubmissionDatesInfo from "./ApplicationSubmissionDatesInfo";
import AddressChangeRequest from "../Admin/User/AddressChangeRequest";
import EventTable from "../Admin/User/EventTable";
import { ProductPill } from "../../common/ProductPills";
import UserInfoBox from "./UserInfoBox";
import { BoostSubscriptionManager } from "./BoostSubscriptionManager";

const copyToClipboard = (text) => {
  if (window.clipboardData && window.clipboardData.setData) {
    // IE specific code path to prevent textarea being shown while dialog is visible.
    return window.clipboardData.setData("Text", text);
  }
  if (
    document.queryCommandSupported &&
    document.queryCommandSupported("copy")
  ) {
    const textarea = document.createElement("textarea");
    textarea.textContent = text;
    textarea.style.position = "fixed"; // Prevent scrolling to bottom of page in MS Edge.
    document.body.appendChild(textarea);
    textarea.select();
    try {
      return document.execCommand("copy"); // Security exception may be thrown by some browsers.
    } catch (ex) {
      console.warn("Copy to clipboard failed.", ex);
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
};

const lookupGalileoCardStatusCode = (code) =>
  ({
    A: "Lost Card Waiting For Sufficient Funds",
    B: "Blocked",
    C: "Canceled",
    D: "Disabled",
    L: "Lost Card",
    N: "Active",
    O: "Operations Hold",
    Q: "Delinquent",
    R: "Charged Off",
    S: "Stolen",
    V: "Voided",
    W: "Waiting for Payment",
    X: "Set to Emboss",
    Y: "Shipped/Ready to Activate",
    Z: "Canceled without Refund",
  }[code] || "Unknown");

const commonRejectionReasons = [
  "User did not pass KYC",
  "One or more bankruptcies on record",
  "We could not verify Financial Institution",
].map((r) => ({ label: r, value: r }));

const Expander = ({ user, sendUpdate, refetch, sortedEventsByLatest }) => {
  const [limit, setLimit] = useState(user.credit_limit);
  const [editingLimit, setEditingLimit] = useState(false);
  const [editingSteps, setEditingSteps] = useState(false);
  const [stepOverrides, setStepOverrides] = useState({});
  const [resetStep, setResetStep] = useState(null);
  const [shouldResendInvite, setShouldResendInvite] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState();
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const [updateUserApplication] = useMutation(UPDATE_USER_APPLICATION);
  const [resetUserApplication] = useMutation(RESET_USER_APPLICATION);
  const [rejectUserApplication] = useMutation(REJECT_USER_APPLICATION);
  const [testUserUnderwrite] = useMutation(TEST_UNDERWRITE);
  const [lostCard] = useMutation(LOST_CARD_RESEND);
  const [reissueCard] = useMutation(REISSUE_CARD);

  const [loading, setLoading] = useState(false);

  const limitEditor = useRef();
  const userCard = user?.card || {};

  const editLimit = () => {
    if (editingLimit) {
      setEditingLimit(false);
      // make network call
    } else {
      setEditingLimit(true);
      setTimeout(() => limitEditor.current.focus());
    }
  };

  const editSteps = () => {
    setEditingSteps(!editingSteps);
  };

  const overrideStep = (step) => {
    const { steps } = user;
    const { number, complete } = step;
    const original = steps.find((s) => s.number === number);
    if (original) {
      if (!!complete !== !!original.complete) { setStepOverrides({ ...stepOverrides, [number]: complete }); } else {
        const { [number]: discard, ...rest } = stepOverrides;
        setStepOverrides({ ...rest });
      }
    } else {
      setStepOverrides({ ...stepOverrides, [number]: complete });
    }
  };

  const [ignoreSentilink, setIgnoreSentilink] = useState(false);
  const [ignoreKyc, setIgnoreKyc] = useState(false);
  const [ignoreUnderwriting, setIgnoreUnderwriting] = useState(false);
  const [ignoreOverdraft, setIgnoreOverdraft] = useState(false);
  const [ignoreBankAge, setIgnoreBankAge] = useState(false);

  const [blockKycAction, setBlockKycAction] = useState(false);
  const [lockReset, setLockReset] = useState(true);
  const [blockResetAction, setBlockResetAction] = useState(false);
  const [blockApproveAction, setBlockApproveAction] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [testStatusResult, setTestStatusResult] = useState("");
  const [testCreditLimitResult, setTestCreditLimitResult] = useState("");

  const [rejectionReason, setRejectionReason] = useState(null);
  const [confirmRejection, setConfirmRejection] = useState(false);
  const [rejectionButtonText, setRejectionButtonText] = useState("Reject");
  const [rejectionResponse, setRejectionResponse] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const resendInvite = async () => {
    const { email } = user;
    console.log(`sending invite to ${email}`);
    // track("DASHBOARD_ADMIN_TRIGGERED_INVITE", {
    //   userInvited: email
    // });

    // setInvitedIds([...invitedIds, email]);

    const jwt = window.localStorage.getItem("jwt");
    setInviting(true);
    axios
      .post(
        "/notifyWaitlistedUser",
        {
          to: email,
        },
        {
          headers: { Authorization: `Bearer ${jwt}` },
        },
      )
      .then((response) => {
        setInviting(false);
        if (response.data.success) {
          setInviteError(null);
          setInviteSuccess(true);
          setTimeout(() => setInviteSuccess(false), 3000);
        } else {
          setInviteError("Error inviting user");
        }
      })
      .catch((error) => {
        console.log(error);
        setInviting(false);
        setInviteError("Error inviting user");
      });
    return false;
  };

  const manualApprove = async () => {
    if (testMode) { return testUnderwrite(); }
    setBlockKycAction(true);
    setBlockApproveAction(true);
    setBlockResetAction(true);

    const jwt = window.localStorage.getItem("jwt");

    const {
      data, // : { approved, pending, reason, hints, error_message, error_code, jwt }
    } = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/v2/getDecision`,
      {
        userId: user.email,
        ignoreSentilink,
        ignoreKyc,
        ignoreOverdraft,
        ignoreBankAge,
        ...(ignoreUnderwriting ? { creditLimit: limit } : {}),
      },
      {
        headers: { Authorization: `Bearer ${jwt}` },
      },
    );

    window.location.reload();

    // for testing
    // const data = { approved: false, pending: true, amount: 123 };

    // const newCompletion = data.approved
    //   ? "approved"
    //   : data.pending
    //   ? "pending"
    //   : undefined;

    // sendUpdate(row, {
    //   ...(newCompletion ? { completion_status: newCompletion } : {}),
    //   ...(data.amount ? { credit_limit: data.amount } : {})
    // });
  };

  const rerunKyc = async () => {
    setBlockKycAction(true);
    setBlockApproveAction(true);
    setBlockResetAction(true);
    const jwt = window.localStorage.getItem("jwt");

    const {
      data: { success, error, reasons },
    } = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/v2/performKyc`,
      {
        email: user.email,
      },
      {
        headers: { Authorization: `Bearer ${jwt}` },
      },
    );
    if (success) {
      setBlockApproveAction(false);

      // sendUpdate(row, {
      //   identity_status: true // approved
      // });
    } else if (error) {
    } else if (reasons) {
    } // TODO: display feedback
  };

  const toggleLockReset = () => {
    setLockReset(!lockReset);
  };

  const updateSteps = async () => {
    const payload = {};
    const { steps, email } = user;
    (steps || []).forEach((s) => {
      const { key, number } = s;
      const changed = number in stepOverrides;
      if (changed) { payload[key] = stepOverrides[number]; }
    });

    setLoading(true);
    updateUserApplication({
      variables: {
        email,
        application: payload,
      },
      // TODO: perform refetch
      // refetchQueries: [{ query: PORTAL_QUERY }],
      // awaitRefetchQueries: true
    })
      .then((res) => {
        console.log("COMPLETED -", res);
        setLoading(false);
        // Assume success
        const updates = (user.steps || []).reduce(
          (a, s) =>
            ((Object.keys(stepOverrides || {}) || []).includes(`${s.number}`)
              ? [...a, { ...s, complete: stepOverrides[s.number] }]
              : [...a, s]),
          [],
        );

        // sendUpdate(row, {
        //   steps: updates
        // });

        setStepOverrides({});
      })
      .catch((e) => {
        console.log("ERROR -", e.message);
        setLoading(false);
      });
  };

  const handleConfirmRejection = (arg) => {
    if (rejectionButtonText === "Confirm?" && !arg) {
      setConfirmRejection(true);
      rejectApplication();
    } else if (arg) {
      setRejectionButtonText("Reject");
    } else {
      setRejectionButtonText("Confirm?");
    }
  };

  const rejectApplication = async () => {
    const { email } = user;
    const reason = (rejectionReason || {}).value;
    console.log("rejecting user", email, "for reason", reason);

    setLoading(true);
    rejectUserApplication({
      variables: {
        userId: email,
        reason,
      },
      // TODO: perform refetch
    })
      .then(async (res) => {
        console.log("REJECTED USER:", res);
        setLoading(false);
        setConfirmRejection(false);
        setRejectionButtonText("Reject");
        setRejectionResponse(
          res.data.rejectUser.message
            ? res.data.rejectUser.message
            : "User sucessfully rejected",
        );
      })
      .catch((e) => {
        console.log("ERROR REJECTING USER", e);
        setLoading(false);
        setConfirmRejection(false);
        setRejectionButtonText("Reject");
        setRejectionResponse("error rejecting user");
      });
  };

  const testUnderwrite = async () => {
    console.log("running underwrite test....");
    const { email } = user;
    // const reason = rejectionReason;
    // console.log("rejecting user", email, "for reason", reason);

    setLoading(true);
    testUserUnderwrite({
      variables: {
        userEmail: email,
        ignoreOverdraft,
        ignoreBankAge,
      },
      // TODO: perform refetch
    })
      .then(async ({ data: { testUnderwrite: res } }) => {
        if (res.status === "OK") {
          setTestStatusResult(res.application_status);
          setTestCreditLimitResult(res.credit_limit);
        } else {
          setTestStatusResult("Error");
          setTestCreditLimitResult("Error");
        }
        setLoading(false);
      })
      .catch((e) => {
        console.log("ERROR Testing underwrite USER", e);
        setLoading(false);
      });
  };

  const resetToStep = async ({ step, all }) => {
    console.log("resetting to step", resetStep);
    const { email } = user;

    setLoading(true);
    resetUserApplication({
      variables: {
        userId: email,
        step: Number(step),
        all,
      },
      // TODO: perform refetch
    })
      .then(async (res) => {
        console.log("COMPLETED RESET STEP:", res);
        setResetStep(null);
        if (shouldResendInvite && (all || Number(step) === 0)) {
          await resendInvite();
        }
        setLoading(false);
      })
      .catch((e) => {
        console.log("ERROR RESETTING STEP", e);
        setLoading(false);
      });
  };

  const toggleTestMode = (bool) => {
    if (testMode !== bool) {
      console.log("should be updating...");
      setIgnoreOverdraft(false);
      setIgnoreBankAge(false);
      setTestMode(bool);
    }
  };

  const admin_actions = [
    "rejected",
    "pending",
    "preapproved",
    "awaiting_user_response",
  ].includes(user.completion_status) ? (
      <div>
        <Container
          className={testMode ? "bg-gray-200" : "bg-white"}
          style={{ width: 500, padding: 0 }}
        >
          <div className="flex w-full">
            <button
              type="button"
              className={classnames(
                "bg-white flex-1 rounded-none outline-0-important py-2",
                { "font-bold": !testMode },
              )}
              onClick={() => toggleTestMode(false)}
            >
            Real
            </button>
            <button
              type="button"
              className={classnames(
                "bg-gray-200 flex-1 rounded-none outline-0-important py-2",
                { "font-bold": testMode },
              )}
              onClick={() => toggleTestMode(true)}
            >
            Test
            </button>
          </div>
          <div className="flex flex-col px-4 pt-4">
            {!testMode && (
              <div>
                <input
                  type="checkbox"
                  className="align-middle mr-2"
                  id="ignoreSentilink"
                  // defaultChecked={ignoreKyc}
                  checked={ignoreSentilink}
                  onChange={(e) => setIgnoreSentilink(!ignoreSentilink)}
                />
                <label htmlFor="ignoreSentilink">Ignore Sentilink</label>
              </div>
            )}
            {!testMode && (
              <div>
                <input
                  type="checkbox"
                  className="align-middle mr-2"
                  id="ignoreKyc"
                  // defaultChecked={ignoreKyc}
                  checked={ignoreKyc}
                  onChange={(e) => setIgnoreKyc(!ignoreKyc)}
                />
                <label htmlFor="ignoreKyc">Ignore KYC</label>
              </div>
            )}
            {!testMode && (
              <div>
                <input
                  type="checkbox"
                  className="align-middle mr-2"
                  id="ignoreUnderwriting"
                  // defaultChecked={ignoreUnderwriting}
                  checked={ignoreUnderwriting}
                  onChange={(e) => setIgnoreUnderwriting(!ignoreUnderwriting)}
                />
                <label htmlFor="ignoreUnderwriting">Ignore underwriting</label>
              </div>
            )}
            <div>
              <input
                type="checkbox"
                className="align-middle mr-2"
                id="ignoreOverdraft"
                // defaultChecked={ignoreOverdraft}
                checked={ignoreOverdraft}
                onChange={(e) => setIgnoreOverdraft(!ignoreOverdraft)}
              />
              <label htmlFor="ignoreOverdraft">Ignore overdraft</label>
            </div>
            <div>
              <input
                type="checkbox"
                className="align-middle mr-2"
                id="ignoreBankAge"
                // defaultChecked={ignoreBankAge}
                checked={ignoreBankAge}
                onChange={(e) => setIgnoreBankAge(!ignoreBankAge)}
              />
              <label htmlFor="ignoreBankAge">Ignore bank age</label>
            </div>
            {!testMode && (
              <div className="flex justify-start my-2">
                <span className="">Rejection reason</span>
                <span className="w-1/2 mx-2">
                  <CreatableSelect
                    placeholder="Optional"
                    name="tags-modal"
                    options={commonRejectionReasons}
                    value={rejectionReason}
                    onChange={(r) => setRejectionReason(r)}
                  />
                </span>
              </div>
            )}
          </div>

          <div className="px-4 py-4">
            {testMode && (
              <div className="py-2">
                <div>
                  <p className="inline">Result: </p>
                  <p className="inline font-bold">{testStatusResult}</p>
                </div>
                <p className="inline">Spending Power: </p>
                <p className="inline">{testCreditLimitResult}</p>
              </div>
            )}
            <button
              type="button"
              className="btn btn-green cursor-pointer px-4 py-2"
              disabled={blockApproveAction || loading}
              onClick={manualApprove}
            >
            Run Decision
            </button>
            {!testMode && (
              <button
                type="button"
                className="btn btn-green cursor-pointer px-4 py-2 ml-4"
                disabled={blockKycAction || loading}
                onClick={rerunKyc}
              >
              Run KYC
              </button>
            )}
            {!testMode && (
              <button
                type="button"
                className="btn btn-green cursor-pointer px-4 py-2 ml-4"
                disabled={blockKycAction || loading}
                onClick={() => setModalOpen(true)}
              >
              Approve
              </button>
            )}
            {!testMode && confirmRejection ? (
              <>
                {!rejectionResponse && (
                  <div className="mb-4 mt-4 pb-4 pt-4">
                    <div
                      className="loader items-center justify-center m-auto"
                      style={{ height: "40px", width: "40px" }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                {!testMode && (
                  <button
                    type="button"
                    className="btn bg-red-600 border-0 cursor-pointer px-4 py-2 ml-4"
                    disabled={loading}
                    onClick={() => handleConfirmRejection()}
                  >
                    {rejectionButtonText}
                  </button>
                )}
              </>
            )}
            {!testMode &&
          rejectionButtonText === "Confirm?" &&
          !confirmRejection ? (
                <button
                  type="button"
                  className="btn bg-black-600 border-0 cursor-pointer px-4 py-2 ml-4"
                  onClick={(e) => handleConfirmRejection(e)}
                >
              X
                </button>
              ) : null}
            {!testMode && rejectionResponse ? (
              <div className="mb-2 mt-2 pb-4 pt-4 text-center">
                {rejectionResponse}
              </div>
            ) : null}
            {!testMode &&
          user.completion_status === COMPLETION_STATUS.REJECTED ? (
                <div className="mb-2 mt-2 pb-4 pt-4 text-center">
              This application has already been rejected.
                </div>
              ) : null}
          </div>
          {/* <div>
        <button
          type="button"
          className="btn btn-green cursor-pointer px-4 py-2 m-4"
          disabled={loading}
          onClick={testUnderwrite}
        >
          Test Underwrite
        </button>
      </div> */}
        </Container>
        <ApproveUserModal
          user={user}
          refetch={refetch}
          isOpen={modalOpen}
          close={() => {
            refetch();
            setModalOpen(false);
          }}
        />
      </div>
    ) : (
      ""
    );

  const Invite = () => {
    console.log("invite success", inviteSuccess);
    if (inviting) { return <p>Processing...</p>; }
    if (inviteSuccess) { return <p>Success!</p>; }
    return (
      <button className="underline" type="button" onClick={resendInvite}>
        Resend invite
      </button>
    );
  };

  const stepSaveButtonDisabled =
    editingSteps || !Object.keys(stepOverrides || {}).length;
  const appMetadata = JSON.parse(user.application?.metadata || null);
  const accountData = JSON.parse(user.accounts);
  const approved = user.completion_status === COMPLETION_STATUS.APPROVED;

  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(false);

  return (
    <>
      <BoostSubscriptionManager email={user.email}/>
      <div className="flex">
        <div className="flex-1 m-2">
          <table className="tomo-table">
            <tbody>
              <tr className={user.rejection_reason ? "border-none" : ""}>
                <th>Identity check</th>
                <td>
                  {
                    { true: "Passed", false: "Failed", null: "N/A" }[
                      user.identity_status
                    ]
                  }
                </td>
              </tr>
              {user.rejection_reason ? (
                <tr>
                  <td
                    colSpan="2"
                    className="text-gray-500 text-sm pt-0"
                    style={{ textAlign: "center" }}
                  >
                    {user.rejection_reason}
                  </td>
                </tr>
              ) : (
                false
              )}
              <tr>
                <th>
                  Spending power{" "}
                  <a
                    className="underline cursor-pointer text-green-primary"
                    onClick={editLimit}
                  >
                    {`(${editingLimit ? "save" : "edit"})`}
                  </a>
                </th>
                <td>
                  {editingLimit ? (
                    <input
                      value={limit}
                      onChange={(e) =>
                        setLimit(isNaN(+e.target.value) ? 0 : +e.target.value)
                      }
                      onKeyDown={(e) => (e.key == "Enter" ? editLimit() : 0)}
                      ref={limitEditor}
                      className="border border-gray-200 rounded-lg p-2"
                      style={{ width: "calc(4ch + 1rem)" }}
                    />
                  ) : limit ? (
                    `$${limit}`
                  ) : (
                    "N/A"
                  )}
                </td>
              </tr>
              <tr className="border-none">
                <th style={{ textAlign: "left" }}>Contact Info</th>
              </tr>
              <tr>
                <td
                  colSpan="2"
                  className="text-gray-500 text-sm pt-0"
                  style={{ textAlign: "left" }}
                >
                  {`phone: ${user.phone_number}`}
                </td>
              </tr>
              <tr className="border-0">
                <th>Card</th>
                <td>{userCard.issued ? "Issued" : "Not Issued"}</td>
              </tr>
              <tr className="text-left border-0">
                <td colSpan="2" className="pt-0" style={{ textAlign: "left" }}>
                  <div className="flex justify-between">
                    {userCard.activated ? `Activated` : "Not Activated"}
                    {userCard.galileo_card_status && (
                      <div className="tooltip">
                        {`Galileo Status: ${userCard.galileo_card_status}`}
                        <span className="tooltiptext">
                          {lookupGalileoCardStatusCode(
                            userCard.galileo_card_status,
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
              {userCard.activated && (
                <tr>
                  <th>Lost Card?</th>
                  <td>
                    <button
                      type="button"
                      className="btn btn-green border-0 p-1 cursor-pointer text-sm bg-gray-400"
                      onClick={async () => {
                        if (
                          window.confirm(
                            "Are you sure to mark this card as lost? A new card will be sent to the shipping address on file",
                          )
                        ) {
                          await lostCard({
                            variables: {
                              user_id: user.email,
                            },
                          });

                          window.location.reload();
                        }
                      }}
                    >
                        Mark as lost
                    </button>
                    {/* <span className="cursor-pointer hover:underline text-green-primary">
                        Mark card as lost
                      </span> */}
                  </td>
                </tr>
              )}
              {userCard.galileo_card_status === "N" && (
                <tr>
                  <th>Reissue Card?</th>
                  <td>
                    {userCard.metadata &&
                      JSON.parse(userCard.metadata)?.reissued ? (
                        <span>Processing...</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-green border-0 p-1 cursor-pointer text-sm bg-gray-400"
                          onClick={async () => {
                            if (
                              window.confirm(
                                "Are you sure to reissue? A new card will be sent to the shipping address on file",
                              )
                            ) {
                              await reissueCard({
                                variables: {
                                  user_id: user.email,
                                },
                              });

                              window.location.reload();
                            }
                          }}
                        >
                          Reissue
                        </button>
                      )}
                  </td>
                </tr>
              )}
              <tr className="border-t">
                <th>Referral Code</th>
                <td>{user.referral_code}</td>
              </tr>
              {/* <tr className="border-t">
                <th>Bank Balances</th>
                <td>
                  <a
                    className="underline hover:text-green-primary"
                    href={`/dashboard/supportal/user?email=${encodeURIComponent(user.email)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Balances
                  </a>
                </td>
              </tr> */}
              {/* <tr className="border-t">
                <th>SSN</th>
                <td>{user.ssn_last_four}</td>
              </tr> */}
              <tr className="border-t border-b-0">
                <th style={{ textAlign: "left" }}>Addresses</th>
              </tr>
              <tr>
                <td colSpan="2" style={{ textAlign: "left" }}>
                  <ReactJson
                    src={(() => {
                      const { __typename, ...rest } =
                        user.billing_address || {};
                      return { ...rest };
                    })()}
                    name="Billing Address"
                    theme="summerfruit:inverted"
                    iconStyle="circle"
                    collapsed
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={false}
                  />
                </td>
              </tr>
              {user.shipping_address ? (
                <tr>
                  <td colSpan="2" style={{ textAlign: "left" }}>
                    <ReactJson
                      src={(() => {
                        const { __typename, ...rest } =
                          user.shipping_address || {};
                        return { ...rest };
                      })()}
                      name="Shipping Address"
                      theme="summerfruit:inverted"
                      iconStyle="circle"
                      collapsed
                      displayDataTypes={false}
                      displayObjectSize={false}
                      enableClipboard={false}
                    />
                  </td>
                </tr>
              ) : (
                <></>
              )}
            </tbody>
          </table>
          <div>
            <Address
              user={user}
              SettingsButton={SettingsButton((response = {}) => {
                if (response.success) {
                  setUpdateSuccess(true);
                  setUpdateError(false);
                }
              })}
              resetSuccess={() => {
                setUpdateSuccess(false);
              }}
              setSuccess={() => {
                setUpdateSuccess(true);
                setUpdateError(false);
              }}
              setError={(error) => {
                setUpdateError(error);
                setUpdateSuccess(false);
              }}
              SettingsInputContainer={SettingsInputContainer}
            />
          </div>
        </div>

        <div className="m-2 text-left">
          <div
            className="shadow"
            style={approved ? { backgroundColor: "#f3f3f3" } : {}}
          >
            <table className="tomo-table">
              <tbody>
                <tr>
                  <th>
                    Step{" "}
                    {approved ? (
                      ""
                    ) : (
                      <a
                        className="underline cursor-pointer text-green-primary"
                        onClick={editSteps}
                      >
                        {`(${editingSteps ? "done" : "edit"})`}
                      </a>
                    )}
                  </th>
                  <th style={{ textAlign: "left" }}>Status</th>
                </tr>
                {(user.steps || []).map((step, i) => {
                  const { name, number, complete } = step;
                  const changed = number in stepOverrides;
                  const value = changed ? !!stepOverrides[number] : !!complete;
                  return (
                    <tr
                      key={i + 1}
                      className={`${changed ? "text-black" : "text-gray-500"}`}
                    >
                      <td className="p-0 py-1 pl-2 text-sm">{`${number}. ${name}`}</td>
                      <td
                        className={`p-0 py-1 pl-4 pr-2 text-sm ${
                          value ? "text-green-primary" : ""
                        }`}
                      >
                        {value ? "complete" : "incomplete"}
                      </td>
                      <td className="p-0">
                        {editingSteps && (
                          <input
                            type="checkbox"
                            className="mr-2"
                            defaultChecked={value}
                            onClick={(e) =>
                              overrideStep({
                                number,
                                complete: e.target.checked,
                              })
                            }
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="w-full text-center">
              <button
                type="button"
                className={`btn btn-green border-0 cursor-pointer py-1 m-2 text-sm ${
                  stepSaveButtonDisabled ? "bg-gray-400" : ""
                }`}
                disabled={stepSaveButtonDisabled}
                onClick={() => {
                  updateSteps();
                }}
              >
                {Object.keys(stepOverrides || {}).length
                  ? "Save Steps"
                  : "Nothing to Save"}
              </button>
            </div>
            <ApplicationSubmissionDatesInfo
              events={sortedEventsByLatest}
              userId={user.id}
            />
          </div>
          <div
            className="mt-2 p-2 shadow text-center"
            style={approved || lockReset ? { backgroundColor: "#f3f3f3" } : {}}
          >
            <div className="font-bold flex justify-between">
              <span>Reset application</span>
              {approved ? (
                <></>
              ) : (
                <span>
                  <label className="switch">
                    <input type="checkbox" onChange={toggleLockReset} />
                    <span className="slider round" />
                  </label>
                </span>
              )}
            </div>
            <div className="my-2">
              <button
                type="button"
                className={`cursor-pointer ${
                  blockResetAction || approved || lockReset
                    ? "text-white"
                    : "text-white bg-red-500 hover:bg-red-600 hover:border-red-500 hover:shadow"
                } m-4 text-sm font-bold px-4 py-1 border`}
                disabled={blockResetAction || approved || lockReset}
                onClick={() => resetToStep({ all: true })}
              >
                Reset Entire Application
              </button>
              <div
                className={`flex items-center justify-center text-sm ${
                  blockResetAction || approved || lockReset
                    ? "text-gray-500"
                    : ""
                }`}
              >
                <input
                  type="checkbox"
                  disabled={blockResetAction || approved || lockReset}
                  className={`mr-2 ${
                    blockResetAction || approved || lockReset
                      ? "cursor-not-allowed"
                      : ""
                  }`}
                  id="resendInvite"
                  checked={shouldResendInvite}
                  onChange={(e) => {
                    setShouldResendInvite(e.target.checked);
                  }}
                />
                <span>Re-send invite </span>
              </div>
            </div>
            <div className="my-2">
              <select
                className={`border py-1 px-2 ${
                  blockResetAction || approved || lockReset
                    ? "cursor-not-allowed"
                    : ""
                }`}
                onChange={(e) => setResetStep(e.target.value || null)}
                disabled={blockResetAction || approved || lockReset}
              >
                <option
                  value={null}
                  defaultValue
                  className={`${
                    approved || blockResetAction || lockReset
                      ? "cursor-not-allowed"
                      : ""
                  }`}
                />
                {(user.steps || []).map((step, i) => (
                  <option key={`step-${i}`} value={step.number}>
                    {step.name}
                  </option>
                ))}
              </select>
              <br />
              <button
                type="button"
                className={`btn btn-green cursor-pointer py-1 mt-2 text-sm ${
                  blockResetAction || approved || lockReset || !resetStep
                    ? "bg-gray-400"
                    : ""
                }`}
                disabled={
                  blockResetAction || approved || lockReset || !resetStep
                }
                onClick={() => resetToStep({ step: resetStep })}
              >
                Reset to selected
              </button>
            </div>
          </div>
          <UserRewardPoints user={user} />
        </div>

        <div className="flex-2 m-2 flex flex-col text-left shadow">
          <div className="p-2 flex-1 border-b">
            <div className="font-bold">
              {`Events ${
                (sortedEventsByLatest || []).length
                  ? `- ${sortedEventsByLatest.length}`
                  : ""
              }`}
            </div>
            {(sortedEventsByLatest || []).length ? (
              sortedEventsByLatest.map((e, i) => (
                <div key={`e-${i}`} className={`${i > 0 ? "mt-1" : ""}`}>
                  <ReactJson
                    src={(() => {
                      const { name, customIdx, ...rest } = e;
                      return { ...rest };
                    })()}
                    name={e.name}
                    theme="summerfruit:inverted"
                    iconStyle="circle"
                    collapsed
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={false}
                  />
                </div>
              ))
            ) : (
              <>No Events</>
            )}
          </div>
          {appMetadata ? (
            <div className="p-2 flex-1">
              <div className="font-bold">Application Metadata</div>
              <ReactJson
                src={appMetadata}
                name={null}
                theme="summerfruit:inverted"
                iconStyle="circle"
                collapsed={2}
                displayDataTypes={false}
                enableClipboard={false}
              />
            </div>
          ) : (
            <></>
          )}
          <div className="p-2 flex-1">
            <div className="font-bold">Card Metadata</div>
            {userCard.metadata && userCard.metadata !== "{}" ? (
              <ReactJson
                src={JSON.parse(userCard.metadata || {})}
                name={null}
                theme="summerfruit:inverted"
                iconStyle="circle"
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={false}
              />
            ) : (
              <>No metadata</>
            )}
            <div className="font-bold">User Metadata</div>
            {user.metadata && !["{}", "null"].includes(user.metadata) ? (
              <ReactJson
                src={JSON.parse(user.metadata || {})}
                name={null}
                theme="summerfruit:inverted"
                iconStyle="circle"
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={false}
              />
            ) : (
              <>No metadata</>
            )}
          </div>
        </div>
      </div>
      <Notes user={user} />
      <AddressChangeRequest user={user} />
      {(accountData?.accounts || []).length &&
      accountData?.accounts.some((acct) => acct.newlyAdded) ? (
          <div className="py-2">
            <div className="font-bold text-green-primary mb-1">
            Newly Added Accounts
            </div>
            {accountData?.accounts
              .filter((acct) => acct.newlyAdded)
              .map((act, i) => (
                <p key={`acct-${i}`}>
                  {`${act.institution} ****${act.mask} (${moment(
                    act.created_at,
                  ).format("MM-DD-YYYY")})`}
                </p>
              ))}
          </div>
        ) : (
          ""
        )}

      <div className="flex-2 m-2 flex flex-col text-left shadow">
        <div className="p-2 flex-1 border-b">
          <div className="font-bold">
            {`Accounts ${
              (accountData?.accounts || []).length
                ? `- ${accountData?.accounts?.length}`
                : ""
            }`}
          </div>
          {(accountData?.accounts || []).length ? (
            <ReactJson
              src={accountData}
              name={null}
              theme="summerfruit:inverted"
              iconStyle="circle"
              collapsed={2}
              displayDataTypes={false}
              enableClipboard={false}
            />
          ) : (
            <>No Accounts</>
          )}
        </div>
        {appMetadata ? (
          <div className="p-2 flex-1">
            <div className="font-bold">Application Metadata</div>
            <ReactJson
              src={appMetadata}
              name={null}
              theme="summerfruit:inverted"
              iconStyle="circle"
              collapsed={2}
              displayDataTypes={false}
              enableClipboard={false}
            />
          </div>
        ) : (
          <></>
        )}
      </div>

      {inviteError && <p>{inviteError}</p>}

      <div className="text-left flex">
        <div className="pl-3 p-1 shadow flex-1">
          <span>Invite Link:</span>
          <span className="ml-4 text-xs text-gray-primary">
            {user.start_link}
          </span>
        </div>
        <div
          className="flex-end border border-black rounded-r bg-gray-200 py-1 px-2 font-bold cursor-pointer hover:bg-gray-300"
          onClick={() => copyToClipboard(user.start_link)}
        >
          copy
        </div>
      </div>

      <div className="text-left flex mt-1 mb-4">
        <div className="pl-3 p-1 shadow flex-1">
          <span>Password Reset Link:</span>
          <span className="ml-4 text-xs text-gray-primary">
            {user.password_reset_link}
          </span>
        </div>
        <div
          className="flex-end border border-black rounded-r bg-gray-200 py-1 px-2 font-bold cursor-pointer hover:bg-gray-300"
          onClick={() => copyToClipboard(user.password_reset_link)}
        >
          copy
        </div>
      </div>

      <div className="flex items-center text-left">
        {admin_actions}
        <div className="flex-1" />
        {user.completion_status === COMPLETION_STATUS.NOT_STARTED && <Invite />}
        <a
          className={`${
            user.alloy_link
              ? "underline hover:text-green-primary"
              : "cursor-not-allowed text-gray-400 hover:text-gray-400"
          } m-4`}
          href={user.alloy_link || (() => {})}
          target="_blank"
          rel="noopener noreferrer"
        >
          Alloy
        </a>
        <a
          className={`${
            user.galileo_prn
              ? "underline hover:text-green-primary"
              : "cursor-not-allowed text-gray-400 hover:text-gray-400"
          } m-4`}
          href={
            user.galileo_prn
              ? `https://example.com/${user.galileo_prn}`
              : () => {}
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          Galileo
        </a>
        <a
          className={`${
            user.sentilink && user.sentilink.transaction_id
              ? "underline hover:text-green-primary"
              : "cursor-not-allowed text-gray-400 hover:text-gray-400"
          } m-4`}
          href={
            user.sentilink &&
            user.sentilink.transaction_id &&
            `https://dashboard.sentilink.com/#/riskops/${user.sentilink.transaction_id}`
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          Sentilink
        </a>
      </div>
    </>
  );
};

const renderStats = (user, actions) => {
  const {
    openModal,
    setUserTags,
    resolveFraudTag,
    newEmail,
    setNewEmail,
    emailUpdated,
    setEmailUpdated,
    refetch,
  } = actions;

  const userTags = (user.tags || []).map((t, i) => (
    <span
      key={`tag-${i}`}
      className="rounded-md border mx-2 px-2 py-1 border-red-400 text-red-500 font-bold cursor-pointer"
      onClick={(e) => {
        openModal(e, "TagModal", {
          setUserTags,
          resolveFraudTag,
          user,
          tag: t,
        });
      }}
    >
      {t}
    </span>
  ));
  // if ((user.tags || []).includes("potential_fraud")) {
  //   const { fraud_reason } = JSON.parse(user.metadata);
  //   if (user.metadata && fraud_reason) {
  //     fraud_reason.forEach(fr => {
  //       console.log(fr)
  //       userTags.push(<span className="rounded-md border mx-2 px-2 py-1 border-red-400 text-red-500 font-bold">{fr}</span>)
  //     });
  //   }
  // }
  userTags.push(
    <div
      key="tag-new"
      className="rounded-md border mx-2 mpx-2 py-1 border-red-400 text-red-500 border-dotted cursor-pointer"
      onClick={(e) => {
        openModal(e, "TagModal", {
          setUserTags,
          resolveFraudTag,
          user,
          tag: null,
        });
      }}
    >
      Add New Tag
    </div>,
  );

  return (
    <div>
      <div>
        <span className="text-4xl font-title font-bold mr-5">
          {user.first_name} {user.last_name}
        </span>
        <div className="flex items-start">
          {(user.products || []).map((product) => {
            return ProductPill({ product });
          })}
        </div>
        <div className="flex items-start my-2">{userTags}</div>
      </div>
      <UpdateUserNames user={user} />
      <div className="flex flex-row mb-8 md:flex-row justify-start">
        <div className="flex mb-4 mx-20 md:mb-0 flex-col md:mx-2">
          <div>
            <div>{emailUpdated ? newEmail : user.email}</div>
            <button
              className="btn-green text-white px-3 py-1 "
              type="button"
              onClick={(e) =>
                openModal(e, "UpdateEmailModal", {
                  user,
                  setEmailUpdated,
                  setNewEmail,
                })
              }
            >
              Edit Email
            </button>
            <div>{user.phone_number}</div>
            <div>
              <span>Status: </span>
              <span className="font-semibold">
                {(user.completion_status || "unknown").toUpperCase()}
              </span>
            </div>
            <div>
              <span>Autopay Group: </span>
              <span className="font-semibold">
                {(
                  DAYS_OF_WEEK[(user.autopay_day || 0) - 1] || "unknown"
                ).toUpperCase()}
              </span>
            </div>
          </div>
        </div>
        <UserInfoBox user={user} refetch={refetch} openModal={openModal} />

        {[COMPLETION_STATUS.APPROVED, COMPLETION_STATUS.TERMINATED].includes(
          user.completion_status,
        ) && (
          <Link
            to={{
              pathname: `/admin/god/user?email=${encodeURIComponent(
                user.email,
              )}`,
            }}
            target="_blank"
          >
            <button
              className="btn btn-green border-0 cursor-pointer py-1 m-2 text-sm"
              type="button"
            >
              Navigate to god view
            </button>
          </Link>
        )}
      </div>

    </div>
  );
};

const renderPage = (
  user,
  openModal,
  { fetchBalances, balancesLoading, refetch },
) => {
  const transactionReport =
    user.transaction_report && JSON.parse(user.transaction_report);
  const assetReport = user.asset_report && user.asset_report.report;
  const hasAssetReport = assetReport || transactionReport;
  const underwritingDecisionReport = user.underwritingDecisionReport
    ? JSON.parse(user.underwritingDecisionReport)
    : {};

  const formattedEvents = (user.events || []).reduce((acc, el, idx) => {
    const { metadata, created_at, updated_at, __typename, ...rest } = el;
    acc.push({
      customIdx: idx,
      metadata: JSON.parse(metadata),
      created_at: new Date(Number(created_at)),
      updated_at: new Date(Number(updated_at)),
      ...rest,
    });
    return acc;
  }, []);
  const sortedEventsByLatest = formattedEvents.sort((a, b) => {
    if (a.created_at === b.created_at) { return 0; }
    return a.created_at < b.created_at ? 1 : -1;
  });

  return (
    <>
      <Expander
        user={user}
        refetch={refetch}
        sortedEventsByLatest={sortedEventsByLatest}
      />
      <FraudDataBox user={user} />
      {hasAssetReport && (
        <AssetReportTable
          report={user.asset_report}
          transactionReport={transactionReport}
          currentAccounts={user.bank_accounts}
        />
      )}
      {hasAssetReport && (
        <UnderwritingDataBox
          assetData={user.asset_report}
          user={user}
          transactionReport={transactionReport}
        />
      )}
      <AccountsList
        user={user}
        openModal={openModal}
        refetch={refetch}
        transactionReport={transactionReport}
        fetchBalances={fetchBalances}
        balancesLoading={balancesLoading}
      />
      <BankIdentityDataBox user={user} />
      <UnderwritingDecisionDataBox
        underwritingDecisionReport={underwritingDecisionReport}
      />
      <TWNTable user={user} />
      <IpAddressTable user={user} />
      <EventTable events={sortedEventsByLatest} />
    </>
  );
};

const SupportalUserView = ({ modalContext: { openModal }, ...props }) => {
  const searchParams = new URLSearchParams(props.location.search);
  const email =
   (searchParams.get("email") || props.match.params.user)?.toLowerCase();
  const { loading, error, data, refetch } = useQuery(QUERY, {
    variables: { userId: email },
    fetchPolicy: "network-only",
  });
  const [refetching, setRefetching] = useState(false);
  const [setUserTags] = useMutation(SET_USER_TAGS);
  const [resolveFraudTag] = useMutation(RESOLVE_FRAUD_TAG);
  const [
    fetchBalances,
    { loading: balancesLoading },
  ] = useLazyQuery(FETCH_BALANCES, {
    fetchPolicy: "network-only",
    onCompleted: async () => {
      setRefetching(true);
      await refetch();
      setRefetching(false);
    },
  });
  const [newEmail, setNewEmail] = useState("");
  const [emailUpdated, setEmailUpdated] = useState(false);

  if (loading) {
    console.log("Loading");
    // return 'Loading...';
    return <>Loading...</>;
  }
  if (error) {
    console.log("Supportal:UserView - error:", error.message);
    // return `Error! ${error.message}`;
    return <>Error...</>;
  }

  return (
    <div className="admin-page">
      <Link to="/supportal" className="flex items-center mb-2">
        <i className="fas fa-chevron-left text-black" />
        <p className="pl-1 underline">Back to Supportal</p>
      </Link>
      {renderStats(data.portalUser, {
        openModal,
        setUserTags,
        resolveFraudTag,
        newEmail,
        setNewEmail,
        emailUpdated,
        setEmailUpdated,
        refetch,
      })}
      {renderPage(data.portalUser, openModal, {
        fetchBalances,
        balancesLoading,
        refetch,
      })}
    </div>
  );
};

export default compose(withModal)(SupportalUserView);
